// Components/CookieConsent.jsx
import { useState, useEffect } from "react";
import { colors } from "../colors";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const CookieConsent = ({ onAccept, onDecline }) => {
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      // Show popup after a small delay for better UX
      setTimeout(() => setShowPopup(true), 1000);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("cookieConsent", "accepted");
    setShowPopup(false);
    onAccept();
  };

  const handleDecline = () => {
    localStorage.setItem("cookieConsent", "declined");
    setShowPopup(false);
    onDecline();
  };

  return (
    <AnimatePresence>
      {showPopup && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-[210]"
          />

          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed top-[27%] left-[5%] md:left-[27%] lg:left-[38%] bg-white rounded-2xl shadow-2xl z-[215] w-[90%] max-w-md mx-auto"
          >
            {/* Content */}
            <div className="p-6 sm:p-8">
              {/* Cookie Icon */}
              <div className="flex justify-center mb-3 sm:mb-4">
                <div
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${colors.primaryGreen}20` }}
                >
                  <span className="text-3xl sm:text-4xl">🍪</span>
                </div>
              </div>

              {/* Title */}
              <h3
                className="text-xl sm:text-2xl font-bold text-center mb-2 sm:mb-3"
                style={{
                  fontFamily: "Bambino",
                  color: colors.primaryRed,
                }}
              >
                Cookie Consent
              </h3>

              {/* Description */}
              <p
                className="text-gray-600 text-center mb-5 sm:mb-6 text-xs sm:text-sm leading-relaxed"
                style={{ fontFamily: "Bambino", fontWeight: 350 }}
              >
                We use cookies to enhance your experience, keep you logged in,
                and remember your cart. Your privacy matters to us.
              </p>

              {/* Buttons */}
              <div className="flex flex-col gap-2.5 sm:gap-3">
                <button
                  onClick={handleAccept}
                  className="w-full py-2.5 sm:py-3 rounded-xl font-semibold text-white transition transform active:scale-95 sm:hover:scale-105 shadow-lg text-sm sm:text-base"
                  style={{
                    backgroundColor: colors.primaryGreen,
                    fontFamily: "Bambino",
                    fontWeight: 450,
                  }}
                >
                  Accept Cookies
                </button>
                <button
                  onClick={handleDecline}
                  className="w-full py-2.5 sm:py-3 rounded-xl font-semibold border-2 transition transform active:scale-95 sm:hover:scale-105 text-sm sm:text-base"
                  style={{
                    borderColor: colors.primaryGreen,
                    color: colors.primaryGreen,
                    fontFamily: "Bambino",
                    fontWeight: 450,
                  }}
                >
                  Decline
                </button>
              </div>

              {/* Privacy note */}
              <p
                className="text-[10px] sm:text-xs text-gray-400 text-center mt-3 sm:mt-4"
                style={{ fontFamily: "Bambino", fontWeight: 350 }}
              >
                Privacy Policy
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;